import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight, List, RotateCcw } from 'lucide-react';
import type { Game } from '../../shared/types';
import { gameTitle } from '../game-presentation';
import { ResponsiveImage } from './ResponsiveImage';
import { MAP_IMAGE_SIZES } from '../../shared/image-delivery';
import {
  gameDestination,
  planIsland,
  readIslandRegistry,
  ISLAND_REGISTRY_KEY,
  ISLAND_ART_ROOT,
  ISLAND_SCENE_ROOT,
  ISLAND_TERRAIN,
  type IslandIcon,
} from '../island-world';

export const ATLAS_ASSETS = ISLAND_ART_ROOT;

export function PlaceIcon({
  icon,
  size = 28,
}: {
  icon: IslandIcon | 'island' | 'trophy' | 'compass';
  size?: number;
}) {
  return (
    <img
      className="atlas-place-art"
      src={`${ATLAS_ASSETS}/icon-${icon}.webp`}
      width={size}
      height={size}
      alt=""
    />
  );
}

export function AtlasMark() {
  return (
    <img
      className="atlas-brand-mark"
      src={`${ATLAS_ASSETS}/island-brand.webp`}
      width="44"
      height="44"
      alt=""
    />
  );
}

function savedRegistry() {
  try {
    return readIslandRegistry(localStorage.getItem(ISLAND_REGISTRY_KEY));
  } catch {
    return {};
  }
}

export function IslandMap({ games, member }: { games: Game[]; member: boolean }) {
  const [initialRegistry] = useState(savedRegistry);
  const registry = useRef(initialRegistry);
  const plan = useMemo(() => planIsland(games, registry.current), [games]);
  const [district, setDistrict] = useState(0);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [labels, setLabels] = useState(false);
  const viewport = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const destinations = useRef<HTMLDetailsElement>(null);
  const drag = useRef({ x: 0, scroll: 0, active: false, moved: false });
  const districts = [...new Set(plan.entries.map((entry) => entry.district))].sort((a, b) => a - b);
  const current = districts.includes(district) ? district : (districts[0] ?? 0);
  const entries = plan.entries.filter((entry) => entry.district === current);
  const currentIndex = districts.indexOf(current);
  const centerMap = () => {
    const el = viewport.current;
    if (el)
      el.scrollTo({
        left: Math.max(0, (el.scrollWidth - el.clientWidth) / 2),
        behavior: 'instant',
      });
  };
  const positionDestinations = () => {
    const details = destinations.current;
    const summary = details?.querySelector('summary');
    const list = details?.querySelector('div');
    if (!details?.open || !summary || !list || failed) return;
    const rect = summary.getBoundingClientRect();
    const visual = window.visualViewport;
    const top =
      Math.max(
        visual?.offsetTop || 0,
        document.querySelector('.island-header')?.getBoundingClientRect().bottom || 0,
      ) + 8;
    const bottom = (visual?.offsetTop || 0) + (visual?.height || innerHeight) - 12;
    if (rect.bottom < top || rect.top > bottom) {
      details.open = false;
      return;
    }
    const left = (visual?.offsetLeft || 0) + 12;
    const width = Math.min(365, (visual?.width || innerWidth) - 24);
    const above = Math.max(0, rect.top - top - 8);
    const below = Math.max(0, bottom - rect.bottom - 8);
    const upward = above >= Math.min(list.scrollHeight, 250) || above > below;
    const height = Math.min(460, list.scrollHeight + 2, upward ? above : below);
    list.style.width = `${width}px`;
    list.style.maxHeight = `${height}px`;
    list.style.left = `${Math.max(left, Math.min(rect.right - width, left + (visual?.width || innerWidth) - 24 - width))}px`;
    list.style.top = `${upward ? rect.top - height - 8 : rect.bottom + 8}px`;
  };
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      if (!failed && destinations.current && !destinations.current.contains(event.target as Node))
        destinations.current.open = false;
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !failed && destinations.current?.open) {
        destinations.current.open = false;
        destinations.current.querySelector('summary')?.focus();
      }
    };
    window.addEventListener('resize', positionDestinations);
    window.addEventListener('scroll', positionDestinations, true);
    window.visualViewport?.addEventListener('resize', positionDestinations);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('resize', positionDestinations);
      window.removeEventListener('scroll', positionDestinations, true);
      window.visualViewport?.removeEventListener('resize', positionDestinations);
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [failed]);
  useEffect(() => {
    registry.current = plan.registry;
    try {
      localStorage.setItem(ISLAND_REGISTRY_KEY, JSON.stringify(plan.registry));
    } catch {
      /* Explore still works without storage. */
    }
  }, [plan]);
  useEffect(() => {
    centerMap();
  }, [current]);
  useEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const observer = new ResizeObserver(centerMap);
    observer.observe(el);
    return () => observer.disconnect();
  }, [failed]);
  const resetMotion = () => {
    stage.current?.style.setProperty('--island-drift-x', '0px');
    stage.current?.style.setProperty('--island-drift-y', '0px');
  };
  return (
    <section
      className={`atlas-map${labels ? ' show-labels' : ''}${failed ? ' atlas-map-failed' : ''}${loaded ? ' atlas-map-loaded' : ''}`}
      aria-label="海岛游戏地图"
    >
      {districts.length > 1 && (
        <div className="atlas-map-status">{current === 0 ? '主岛' : `岛区 ${current + 1}`}</div>
      )}
      {!failed ? (
        <div
          className="atlas-map-viewport"
          ref={viewport}
          tabIndex={0}
          role="region"
          aria-label="可横向滚动的岛屿地图"
          aria-describedby="atlas-map-help"
          onPointerDown={(event) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) return;
            drag.current = {
              x: event.clientX,
              scroll: event.currentTarget.scrollLeft,
              active: true,
              moved: false,
            };
          }}
          onPointerMove={(event) => {
            if (drag.current.active && event.buttons === 1) {
              const delta = event.clientX - drag.current.x;
              if (Math.abs(delta) > 7) drag.current.moved = true;
              if (drag.current.moved) event.currentTarget.scrollLeft = drag.current.scroll - delta;
            } else if (
              event.pointerType === 'mouse' &&
              !window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ) {
              const rect = event.currentTarget.getBoundingClientRect();
              stage.current?.style.setProperty(
                '--island-drift-x',
                `${((event.clientX - rect.left) / rect.width - 0.5) * 5}px`,
              );
              stage.current?.style.setProperty(
                '--island-drift-y',
                `${((event.clientY - rect.top) / rect.height - 0.5) * 3}px`,
              );
            }
          }}
          onPointerUp={() => {
            drag.current.active = false;
          }}
          onPointerCancel={() => {
            drag.current.active = false;
            drag.current.moved = false;
          }}
          onPointerLeave={() => {
            drag.current.active = false;
            resetMotion();
          }}
          onClickCapture={(event) => {
            if (drag.current.moved) {
              event.preventDefault();
              event.stopPropagation();
              drag.current.moved = false;
            }
          }}
        >
          <div className="atlas-map-canvas" ref={stage}>
            <ResponsiveImage
              className="atlas-map-art"
              src={ISLAND_TERRAIN.src}
              sizes={MAP_IMAGE_SIZES}
              alt="雪山、草地与临海悬崖组成的立体岛屿"
              width={ISLAND_TERRAIN.width}
              height={ISLAND_TERRAIN.height}
              fetchPriority="high"
              loading="eager"
              decoding="async"
              draggable={false}
              onLoad={() => {
                setLoaded(true);
                centerMap();
              }}
              onError={() => setFailed(true)}
            />
            {entries.map(({ game, place, model, slot }) => (
              <Link
                key={game.id}
                className={`atlas-landmark${place.featured && model !== 'game' ? ' is-featured' : ''}`}
                style={{ left: `${place.x}%`, top: `${place.y}%` }}
                to={gameDestination(game, member)}
                data-game-id={game.id}
                data-plot={slot}
                data-model={model}
                aria-label={`${place.name}：${gameTitle(game)}，${game.free || member ? '开始游戏' : '查看详情'}`}
                draggable={false}
              >
                <span className="atlas-ground-shadow" />
                <img
                  className="atlas-building"
                  style={{ zIndex: Math.round(place.y) }}
                  src={`${ISLAND_SCENE_ROOT}/landmark-${model}${model === 'sheep' || model === 'fruit' ? '-landscape' : ''}.webp`}
                  width="256"
                  height="256"
                  alt=""
                  draggable={false}
                  onError={(event) => {
                    if (event.currentTarget.dataset.fallback !== 'true') {
                      event.currentTarget.dataset.fallback = 'true';
                      event.currentTarget.src = `${ISLAND_SCENE_ROOT}/landmark-game.webp`;
                    }
                  }}
                />
                <span className="atlas-landmark-target" aria-hidden="true" />
                <span className="atlas-landmark-label">
                  <strong title={gameTitle(game)}>{gameTitle(game)}</strong>
                  <ArrowUpRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <p className="atlas-map-unavailable">海岛暂时未能加载，可以从地点列表进入游戏。</p>
      )}
      <div className="atlas-map-controls">
        <p id="atlas-map-help" className="atlas-map-help">
          点击建筑进入游戏，横向滚动可查看全岛。
        </p>
        {districts.length > 1 && (
          <div className="atlas-district-switch" role="group" aria-label="切换岛区">
            <button
              aria-label="上一个岛区"
              disabled={currentIndex <= 0}
              onClick={() => setDistrict(districts[currentIndex - 1])}
            >
              <ChevronLeft size={16} />
            </button>
            <span>
              {currentIndex + 1} / {districts.length}
            </span>
            <button
              aria-label="下一个岛区"
              disabled={currentIndex >= districts.length - 1}
              onClick={() => setDistrict(districts[currentIndex + 1])}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
        {!failed && (
          <>
            <button className="atlas-center-map" aria-label="回到海岛中心" onClick={centerMap}>
              <RotateCcw size={15} />
            </button>
            <button aria-pressed={labels} onClick={() => setLabels(!labels)}>
              {labels ? '收起名称' : '显示名称'}
            </button>
          </>
        )}
        <details
          className="atlas-destinations"
          ref={destinations}
          open={failed || undefined}
          onToggle={positionDestinations}
        >
          <summary>
            <List size={14} />
            全部地点
          </summary>
          <div>
            {plan.entries.map(({ game, place }) => (
              <Link key={game.id} to={gameDestination(game, member)}>
                <PlaceIcon icon={place.icon} size={28} />
                <span>
                  {gameTitle(game)}
                  <small>{place.name}</small>
                </span>
                <ArrowUpRight size={13} />
              </Link>
            ))}
          </div>
        </details>
      </div>
    </section>
  );
}
